'use client';

import type { Account } from '@/types/v2/account';
import type { Community } from '@/types/v2/community';
import type { User } from '@/types/v2/user';
import { config } from '@/config';
import axios from '@/lib/v2/axios';
import type { Unit } from '@/types/v2/unit';

export interface SignUpParams {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface SignInWithPasswordParams {
  email: string;
  password: string;
}

export interface ResetPasswordParams {
  email: string;
}

export interface ValidatePasswordResetParams {
  token: string;
  email: string;
}

export interface ConfirmPasswordResetParams {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface ChangePasswordParams {
  current_password: string;
  password: string;
  password_confirmation: string;
}

const csrf = () => axios.get('/sanctum/csrf-cookie');

class AuthClient {
  async signUp(_: SignUpParams): Promise<{ error?: string }> {
    // Make API request

    // We do not handle the API, so we'll just generate a token and store it in localStorage.

    return {};
  }

  async signInWithPassword(params: SignInWithPasswordParams): Promise<{ error?: string }> {
    const { email, password } = params;

    try {
      await csrf();
      const response = await axios.post(`/api/v2/auth/login`, {
        email,
        password,
      });

      if (response?.data?.error) {
        return { error: response.data.error };
      }

      const token = response.data.token;

      localStorage.setItem(config.api.token, token);

      return {};
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 401) {
        return { error: error.response.data.error };
      }

      if (error.response?.status === 422) {
        return { error: error.response.data.message };
      }

      return { error: 'An unexpected error ocurred' };
    }
  }

  async resetPassword(params: ResetPasswordParams): Promise<{ error?: string }> {
    try {
      await csrf();
      const response = await axios.post('/api/v2/auth/password/reset', params);

      if (response?.data?.error) {
        return { error: response.data.error };
      }

      return {};
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 422) {
        return { error: error.response.data.message };
      }

      return { error: 'An unexpected error occurred' };
    }
  }

  async validatePasswordReset(params: ValidatePasswordResetParams): Promise<{ error?: string; valid?: boolean }> {
    try {
      await csrf();
      const response = await axios.post('/api/v2/auth/password/reset/validate', params);

      if (response?.data?.error) {
        return { error: response.data.error };
      }

      return { valid: response.data.valid };
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 400) {
        return { error: error.response.data.message };
      }
      if (error.response?.status === 422) {
        return { error: error.response.data.message };
      }

      return { error: 'An unexpected error occurred' };
    }
  }

  async confirmPasswordReset(params: ConfirmPasswordResetParams): Promise<{ error?: string; token?: string }> {
    try {
      await csrf();
      const response = await axios.post('/api/v2/auth/password/reset/confirm', params);

      if (response?.data?.error) {
        return { error: response.data.error };
      }

      const token = response.data.token;
      if (token) {
        localStorage.setItem(config.api.token, token);
      }

      return { token };
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 422) {
        return { error: error.response.data.message };
      }

      return { error: 'An unexpected error occurred' };
    }
  }

  async getUser(): Promise<{
    data: {
      user: User | null;
      account: Account | null;
      communities: Community[] | null;
      community: Community | null;
      units: Unit[] | null;
    } | null;
    error?: string;
  }> {
    const token = localStorage.getItem(config.api.token);

    if (!token) {
      return { data: null };
    }

    try {
      const response = await axios.get('/api/v2/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { data } = response;

      const account = data?.account;
      const communities = data?.communities;
      const community = data?.community;
      const units = data?.units;
      const user = {
        id: data.id,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        avatar: data.avatar,
        roles: data.roles,
        permissions: data.permissions,
        selected_community_id: data.selected_community_id,
        selected_account_id: data.selected_account_id,
        account_roles: data.account_roles,
        community_roles: data.community_roles,
        created_at: data.created_at,
        updated_at: data.updated_at,
        password_change_required: data.password_change_required,
      };

      const owner = data?.account?.owner_id === data?.id;
      return {
        data: {
          user: {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone: user.phone,
            avatar: user.avatar,
            roles: user.roles,
            permissions: user.permissions,
            account_roles: user.account_roles,
            community_roles: user.community_roles,
            selected_account_id: user.selected_account_id,
            selected_community_id: user.selected_community_id,
            created_at: user.created_at,
            updated_at: user.updated_at,
            password_change_required: user.password_change_required,
          },
          account,
          communities,
          community,
          units,
        },
      };
    } catch (error: any) {
      return { data: null };
    }
  }

  async signOut(): Promise<{ error?: string }> {
    localStorage.removeItem(config.api.token);

    return {};
  }

  async setUserCommunity(community: Community): Promise<{ error?: string }> {
    const token = localStorage.getItem(config.api.token);

    if (!token) {
      return { error: 'Unauthorized' };
    }

    await csrf();
    await axios.put(`/api/v2/auth/user`, {
      selected_community_id: community.id,
    });

    return {};
  }

  async changePassword(params: ChangePasswordParams): Promise<{ error?: string }> {
    try {
      await csrf();
      await axios.put(`/api/v2/auth/password`, params);

      return {};
    } catch (error: any) {
      return { error: error.response.data.message };
    }
  }
}

export const authClient = new AuthClient();
